import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, UserRound, CalendarClock, CheckCircle2, PlayCircle, ArrowRight } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/apiClient";
import { Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EXPERIENCE = ["Just starting out", "Some freelancing experience", "Experienced VA"];
const ENGLISH = ["Basic", "Conversational", "Fluent"];

function BookingFlow({ type, cfg, onClose }) {
  const isGroup = type === "group";
  const data = isGroup ? cfg.group : cfg.one;
  const [form, setForm] = useState({ name: "", email: "", experience_level: "", english_level: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please enter your name and email");
    if (!form.experience_level) return toast.error("Please select your experience level");
    if (!form.english_level) return toast.error("Please select your English level");
    setBusy(true);
    try {
      await api.post("/mentorship/book", { session_type: type, ...form });
      setDone(true);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid={`booking-flow-${type}`}>
        <DialogHeader><DialogTitle>Book {data.title}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-center py-6" data-testid="booking-success">
            <CheckCircle2 className="w-14 h-14 text-brandgreen mx-auto mb-3" />
            <p className="font-heading font-bold text-lg">Booking submitted!</p>
            <p className="text-sm text-muted-foreground mt-1">Coach Reyland will review your request and reply inside your message inbox on the website. Your seat is confirmed once your payment is marked as paid.</p>
            <Button className="rounded-full bg-teal hover:bg-teal/90 mt-5" onClick={onClose} data-testid="booking-done">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Full name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="book-name" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="book-email" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Experience level *</Label>
                <Select value={form.experience_level} onValueChange={(v) => set("experience_level", v)}>
                  <SelectTrigger data-testid="book-experience"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{EXPERIENCE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>English level *</Label>
                <Select value={form.english_level} onValueChange={(v) => set("english_level", v)}>
                  <SelectTrigger data-testid="book-english"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{ENGLISH.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Anything you want to work on? (optional)</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="book-note" /></div>
            <div className="rounded-xl bg-secondary/50 p-4 text-center">
              <p className="font-semibold text-sm">Pay {data.price} then submit your proof</p>
              {data.qr_url ? <img src={data.qr_url.startsWith("http") ? data.qr_url : fileUrl(data.qr_url)} alt="Payment QR" className="w-44 h-44 object-contain mx-auto my-3 border border-border rounded-lg bg-white" data-testid="book-qr" />
                : <p className="text-xs text-muted-foreground my-3">Payment QR will be shared by the coach in your inbox.</p>}
              <p className="text-xs text-muted-foreground">After you submit, Coach Reyland confirms your seat once your payment is received.</p>
            </div>
            <Button className="rounded-full bg-teal hover:bg-teal/90 w-full" onClick={submit} disabled={busy} data-testid="book-submit">Submit Booking</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function BookSession() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [flow, setFlow] = useState(null);
  useEffect(() => { api.get("/mentorship/booking-info").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto">
      <Button variant="ghost" className="rounded-full mb-4 -ml-2" onClick={() => nav("/")} data-testid="book-back"><ArrowLeft className="w-4 h-4 mr-1.5" /> Home</Button>
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal bg-teal/10 px-3 py-1.5 rounded-full"><CalendarClock className="w-3.5 h-3.5" /> Book a Mentorship</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-3 text-navy">Mentorship with Coach Reyland</h1>
        <p className="text-muted-foreground mt-2">Choose the option that fits you best.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {/* GROUP */}
        <div className="bg-white border border-emerald-100 rounded-2xl p-7 shadow-sm flex flex-col" data-testid="session-card-group">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal to-brandgreen text-white flex items-center justify-center mb-4"><Users className="w-6 h-6" /></div>
          <h2 className="font-heading text-xl font-bold text-navy">{cfg.group.title}</h2>
          <p className="text-teal font-heading text-2xl font-extrabold mt-1">{cfg.group.price}</p>
          {typeof cfg.group.seats_left === "number" && (
            <p className="text-sm font-bold mt-1 text-red-600" data-testid="group-seats-left">
              {cfg.group.seats_left > 0 ? `Only ${cfg.group.seats_left} seat${cfg.group.seats_left === 1 ? "" : "s"} left` : "Fully booked"}
            </p>
          )}
          <p className="text-muted-foreground mt-2 whitespace-pre-wrap flex-1">{cfg.group.description}</p>
          {cfg.group.policies && <p className="text-xs text-muted-foreground mt-3 whitespace-pre-wrap"><b>Policies:</b> {cfg.group.policies}</p>}
          <Button className="rounded-full bg-teal hover:bg-teal/90 mt-5" onClick={() => setFlow("group")} data-testid="book-now-group">Book a Group Mentorship</Button>
        </div>

        {/* 1-ON-1 */}
        <div className="bg-white border border-emerald-100 rounded-2xl p-7 shadow-sm flex flex-col" data-testid="session-card-one">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal to-brandgreen text-white flex items-center justify-center mb-4"><UserRound className="w-6 h-6" /></div>
          <h2 className="font-heading text-xl font-bold text-navy">{cfg.one.title}</h2>
          <p className="text-teal font-heading text-2xl font-extrabold mt-1">{cfg.one.price}</p>
          <p className="text-muted-foreground mt-2 whitespace-pre-wrap flex-1">{cfg.one.description}</p>
          {cfg.one.policies && <p className="text-xs text-muted-foreground mt-3 whitespace-pre-wrap"><b>Policies:</b> {cfg.one.policies}</p>}
          <Button className="rounded-full bg-teal hover:bg-teal/90 mt-5" onClick={() => setFlow("one")} data-testid="book-now-one">Book a 1-on-1 Mentorship</Button>
        </div>

        {/* UPWORK PLAYBOOK */}
        <div className="bg-navy text-white rounded-2xl p-7 shadow-sm flex flex-col relative overflow-hidden" data-testid="session-card-playbook">
          <div className="w-12 h-12 rounded-2xl bg-white/15 text-white flex items-center justify-center mb-4"><PlayCircle className="w-6 h-6" /></div>
          <h2 className="font-heading text-xl font-bold">The Upwork Playbook</h2>
          <p className="text-teal-soft font-semibold text-sm mt-1">Premium Video Walkthrough</p>
          <p className="text-white/70 mt-2 flex-1">Coach Reyland's complete step-by-step video walkthrough to winning clients on Upwork — watch at your own pace.</p>
          <Button className="rounded-full bg-teal hover:bg-teal/90 mt-5" onClick={() => nav("/video")} data-testid="get-playbook">Get the Upwork Playbook <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </div>
      {flow && <BookingFlow type={flow} cfg={cfg} onClose={() => setFlow(null)} />}
    </div>
  );
}
